// The call I am in, at the foot of every sidebar (a row: its title, the space,
// how many are in it, whether someone shares a screen, and the elapsed time),
// in the folded column (the bars, its title and time on hover) and at the top
// of the phone's panel. Pressing it opens the call. Nothing while not in a call.
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { m } from "@trameria/cloud-i18n";
import {
  type OngoingProps,
  type ShellOngoing,
  Tooltip,
  useApi,
  useShellFrame,
} from "@trameria/ui-core";
import { spacesQuery } from "../workspace";
import { EqBars, elapsedLabel, peopleOf, useSecondClock } from "./bits";
import { useVoice, type VoiceSession } from "./lib/session";
import { voicesQuery } from "./queries";

export function JoinedCall({
  session,
  shape,
  onGo,
}: OngoingProps & { session: VoiceSession }) {
  const voice = useVoice(session);
  const on =
    voice.status === "joining" ||
    voice.status === "joined" ||
    voice.status === "reconnecting";
  if (!on || !voice.slug || !voice.roomId) return null;
  return (
    <JoinedRow
      slug={voice.slug}
      roomId={voice.roomId}
      startedAt={voice.startedAt}
      sharing={voice.screen !== null || voice.peers.some((p) => p.sharing)}
      shape={shape}
      onGo={onGo}
    />
  );
}

/**
 * The same row as data (`ShellOngoing`), for a host that draws the sidebar:
 * the call's title, its space, how many are in it and whether a screen is
 * shared, and when it began. Null while not in a call.
 */
export function useJoinedCallData(session: VoiceSession): ShellOngoing | null {
  const voice = useVoice(session);
  const api = useApi();
  const on =
    voice.status === "joining" ||
    voice.status === "joined" ||
    voice.status === "reconnecting";
  const slug = on ? voice.slug : null;
  const roomId = on ? voice.roomId : null;
  const room = useQuery({
    ...voicesQuery(api, slug ?? ""),
    enabled: slug !== null,
  }).data?.find((r) => r.id === roomId);
  const space = useQuery({
    ...spacesQuery(api, slug ?? ""),
    enabled: slug !== null,
  }).data?.find((s) => s.id === room?.spaceId);
  if (!slug || !roomId || !room) return null;
  const sharing = voice.screen !== null || voice.peers.some((p) => p.sharing);
  const count = peopleOf(room.participants, true).length;
  return {
    id: `call:${roomId}`,
    label: room.title,
    icon: "voice",
    to: `/${slug}/calls/${roomId}`,
    detail: [
      space?.name,
      m.vc_people({ count }),
      sharing ? m.vc_sharing() : null,
    ]
      .filter(Boolean)
      .join(" · "),
    ...(voice.startedAt
      ? { since: new Date(voice.startedAt).toISOString() }
      : {}),
  };
}

function JoinedRow({
  slug,
  roomId,
  startedAt,
  sharing,
  shape,
  onGo,
}: {
  slug: string;
  roomId: string;
  startedAt: number | null;
  sharing: boolean;
} & OngoingProps) {
  const api = useApi();
  const navigate = useNavigate();
  // A phone has no sidebar: the row is in its panel.
  const { phone } = useShellFrame();
  const now = useSecondClock(true);
  const room = useQuery(voicesQuery(api, slug)).data?.find(
    (r) => r.id === roomId,
  );
  const space = useQuery(spacesQuery(api, slug)).data?.find(
    (s) => s.id === room?.spaceId,
  );
  // Shown once the list has the call (the session does not hold its title).
  if (!room) return null;
  const elapsed = startedAt ? elapsedLabel(startedAt, now) : "";
  const go = () => {
    void navigate({ to: `/${slug}/calls/${roomId}` });
    onGo?.();
  };
  if (shape === "icon")
    return (
      <Tooltip text={`${room.title} · ${elapsed}`} side="right">
        <button
          type="button"
          data-row="voice"
          aria-label={m.vc_joined_aria({ title: room.title })}
          onClick={go}
          className="inline-flex h-8 w-9 flex-none cursor-pointer items-center justify-center rounded-[7px] bg-live-soft"
        >
          <EqBars small />
        </button>
      </Tooltip>
    );
  const count = peopleOf(room.participants, true).length;
  // The bars stand in the rows' icon box and the title starts where their
  // names do: the sidebar's rows (10 px in, a 16 px icon, 10 px gap) or the
  // phone's panel's (12 px, 20 px, 12 px).
  return (
    <button
      type="button"
      data-row="voice"
      aria-label={m.vc_joined_aria({ title: room.title })}
      onClick={go}
      className={`flex w-full flex-none cursor-pointer items-center rounded-[7px] bg-live-soft py-[7px] text-left ${
        phone ? "gap-3 px-3" : "gap-[10px] px-[10px]"
      }`}
    >
      <span
        className={`flex flex-none justify-center ${phone ? "w-5" : "w-4"}`}
      >
        <EqBars small />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12px] text-body">
          {room.title}
        </span>
        <span className="mt-px block truncate text-[10.5px] text-muted">
          {[
            space?.name,
            m.vc_people({ count }),
            sharing ? m.vc_sharing() : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </span>
      <span className="flex-none text-[11.5px] text-muted tabular-nums">
        {elapsed}
      </span>
    </button>
  );
}
